import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, MailIcon } from 'lucide-react';
type Status = 'idle' | 'submitting' | 'success' | 'error';
interface NewsletterFormProps {
  tone: 'light' | 'dark';
  id: string;
}
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export function NewsletterForm({
  tone,
  id
}: NewsletterFormProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const isDark = tone === 'dark';
  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!EMAIL_PATTERN.test(email.trim())) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    await new Promise(resolve => setTimeout(resolve, 700));
    setStatus('success');
  }
  if (status === 'success') {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.96
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.25,
      ease: [0.23, 1, 0.32, 1]
    }} className={['flex items-start gap-3 rounded-2xl p-5', isDark ? 'bg-white/10 text-white' : 'bg-white ring-1 ring-ink/10'].join(' ')} role="status" data-id="element-89" data-mp="z2a0mkgurc4i">
        <span className={['mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full', isDark ? 'bg-lime text-forest' : 'bg-forest text-white'].join(' ')} data-id="element-90" data-mp="75eb11y85a30">
          <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-91" data-mp="3udi1kkzd65rl" />
        </span>
        <div data-id="element-92" data-mp="3pjnldriyw8x9">
          <p className={isDark ? 'font-bold text-white' : 'font-bold text-ink'} data-id="element-93" data-mp="3pj5em0yoicb3">
            Du er tilmeldt
          </p>
          <p className={['mt-1 text-sm leading-relaxed', isDark ? 'text-white/70' : 'text-muted'].join(' ')} data-id="element-94" data-mp="3fn2cuw7qofoo">
            Vi sender en mail til {email.trim()}, så snart et nyt lån åbner på
            primærmarkedet.
          </p>
        </div>
      </motion.div>;
  }
  return <form onSubmit={handleSubmit} noValidate className="w-full" data-id="element-95" data-mp="2wwxa3f6wx3wm">
      <label htmlFor={id} className="sr-only" data-id="element-96" data-mp="310lke5mxidlk">
        Din e-mailadresse
      </label>
      <div className="flex flex-col gap-3 sm:flex-row" data-id="element-97" data-mp="2n0khkkn3sw3g">
        <div className="relative flex-1" data-id="element-98" data-mp="2n25a64puy4yo">
          <MailIcon className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-99" data-mp="2rz5m2w9e21xp" />
          <input id={id} type="email" name="email" autoComplete="email" placeholder="Din e-mailadresse" value={email} aria-invalid={status === 'error'} aria-describedby={status === 'error' ? `${id}-error` : undefined} onChange={event => {
          setEmail(event.target.value);
          if (status === 'error') setStatus('idle');
        }} className={['h-14 w-full rounded-full bg-white pl-14 pr-5 text-base text-ink placeholder:text-muted transition-colors duration-150 ease-smooth focus:outline-none focus-visible:ring-2 focus-visible:ring-brand', status === 'error' ? 'ring-2 ring-red-500' : 'ring-1 ring-ink/15 focus-visible:ring-brand'].join(' ')} data-id="element-100" data-mp="3fr2sg7u2k1rf" />
        </div>
        <button type="submit" disabled={status === 'submitting'} className={['h-14 shrink-0 rounded-full px-8 text-base font-bold transition-colors duration-150 ease-smooth focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-70', isDark ? 'bg-lime text-forest hover:bg-white focus-visible:outline-lime' : 'bg-brand text-white hover:bg-brand-dark focus-visible:outline-brand'].join(' ')} data-id="element-101" data-mp="1wr3j3d7qg7h7">
          {status === 'submitting' ? 'Tilmelder…' : 'Tilmeld'}
        </button>
      </div>

      <AnimatePresence initial={false} data-id="element-102" data-mp="3jro30g46xpex">
        {status === 'error' && <motion.p id={`${id}-error`} initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} className={['mt-3 text-sm font-medium', isDark ? 'text-lime' : 'text-red-600'].join(' ')} data-id="element-103" data-mp="3jtr1zpnbmsvs">
            Indtast en gyldig e-mailadresse.
          </motion.p>}
      </AnimatePresence>
    </form>;
}